<!-- Template Section -->
<template>
  <div class="accordion-container">
    <div class="d-flex flex-column mt-4">
      <!-- Email Notifications Section -->
      <div class="text-h6 font-weight-bold" style="color: #424242;">
        Email notifications should be sent to:
      </div>
      <div class="ms-8 mt-4">
        <div v-if="editingEmails" class="d-flex align-center">
          <v-col cols="8">
            <v-text-field
              v-model="editingEmailsText"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="one or more email addresses separated by commas..."
              @keyup.enter="saveEmailEdit"
              @keyup.esc="cancelEmailEdit"
              ref="emailInput"
            ></v-text-field>
          </v-col>
          <v-icon 
            class="ms-2" 
            color="success" 
            size="small"
            @click="saveEmailEdit"
          >
            mdi-check
          </v-icon>
          <v-icon 
            class="ms-2" 
            color="error" 
            size="small"
            @click="cancelEmailEdit"
          >
            mdi-close
          </v-icon>
        </div>
        
        <div v-else class="d-flex align-center">
          <span class="item-text">{{ company?.intakeNotificationEmails || 'None configured' }}</span>
          <v-icon 
            class="ms-2 edit-icon" 
            size="small"
            @click="startEmailEdit"
          >
            mdi-pencil
          </v-icon>
        </div>
      </div>

      <!-- SMS Notifications Section -->
      <div class="text-h6 font-weight-bold mt-6" style="color: #424242;">
        SMS (text) notifications should be sent to:
      </div>
      <div class="ms-8 mt-4">
        <div v-if="editingSMS" class="d-flex align-center">
          <v-col cols="8">
            <v-text-field
              v-model="editingSMSText"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="one or more phone numbers in any format separated commas..."
              @keyup.enter="saveSMSEdit"
              @keyup.esc="cancelSMSEdit"
              ref="smsInput"
            ></v-text-field>
          </v-col>
          <v-icon 
            class="ms-2" 
            color="success" 
            size="small"
            @click="saveSMSEdit"
          >
            mdi-check
          </v-icon>
          <v-icon 
            class="ms-2" 
            color="error" 
            size="small"
            @click="cancelSMSEdit"
          >
            mdi-close
          </v-icon>
        </div>
        
        <div v-else class="d-flex align-center">
          <span class="item-text">{{ company?.smsNotificationNumbers || 'None configured' }}</span>
          <v-icon 
            class="ms-2 edit-icon" 
            size="small"
            @click="startSMSEdit"
          >
            mdi-pencil
          </v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick } from 'vue';
import axios from 'axios';

export default {
  name: 'NotificationDetails',
  components: {},
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  emits: ['update:company', 'show-dialog'],
  setup(props, { emit }) {
    // Email editing state
    const editingEmails = ref(false);
    const editingEmailsText = ref('');
    const emailInput = ref(null);

    // SMS editing state
    const editingSMS = ref(false);
    const editingSMSText = ref('');
    const smsInput = ref(null);

    // Email editing methods
    const startEmailEdit = async () => {
      editingEmails.value = true;
      editingEmailsText.value = props.company?.intakeNotificationEmails || '';
      await nextTick();
      emailInput.value?.focus();
    };

    const cancelEmailEdit = () => {
      editingEmails.value = false;
      editingEmailsText.value = '';
    };

    const saveEmailEdit = async () => {
      try {
        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { intakeNotificationEmails: editingEmailsText.value }
        );

        if (response.data.success) {
          emit('update:company', {
            intakeNotificationEmails: editingEmailsText.value
          });
          editingEmails.value = false;
          emit('show-dialog', 'Your changes were successfully saved.');
        } else {
          emit('show-dialog', 'There was an error saving your changes. Please try again.');
        }
      } catch (error) {
        console.error('Error saving email notifications:', error);
        emit('show-dialog', 'There was an error saving your changes. Please try again.');
      }
    };

    // SMS editing methods
    const startSMSEdit = async () => {
      editingSMS.value = true;
      editingSMSText.value = props.company?.smsNotificationNumbers || '';
      await nextTick();
      smsInput.value?.focus();
    };

    const cancelSMSEdit = () => {
      editingSMS.value = false;
      editingSMSText.value = '';
    };

    const saveSMSEdit = async () => {
      try {
        const response = await axios.put(
          `/portaldb/db/updateSmsProperties/${props.company.companyNumber}`,
          { smsNotificationNumbers: editingSMSText.value }
        );

        if (response.data.success) {
          emit('update:company', {
            smsNotificationNumbers: editingSMSText.value
          });
          editingSMS.value = false;
          emit('show-dialog', 'Your changes were successfully saved.');
        } else {
          emit('show-dialog', 'There was an error saving your changes. Please try again.');
        }
      } catch (error) {
        console.error('Error saving SMS notifications:', error);
        emit('show-dialog', 'There was an error saving your changes. Please try again.');
      }
    };

    return {
      // Email
      editingEmails,
      editingEmailsText,
      emailInput,
      startEmailEdit,
      cancelEmailEdit,
      saveEmailEdit,
      // SMS
      editingSMS,
      editingSMSText,
      smsInput,
      startSMSEdit,
      cancelSMSEdit,
      saveSMSEdit
    };
  }
};
</script>

<style scoped>
.accordion-container {
  background-color: #f5f5dc;
  padding: 20px;
  border-radius: 8px;
  min-height: 200px;
}

.edit-icon {
  opacity: 0.3;
  transition: opacity 0.2s ease;
}

.edit-icon:hover {
  opacity: 1;
}

.item-text {
  line-height: 20px;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: bold;
}

:deep(.v-text-field input) {
  font-size: 15px !important;
}
</style>

